import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import LoadingSkeleton from '../components/LoadingSkeleton';
import { useAuth } from '../context/AuthContext';
import { apiCall } from '../services/api.service';
import { API_CONFIG } from '../config/apiConfig';

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const { token } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchOrderDetail = async () => {
      if (!orderId || !token) {
        setLoading(false);
        return;
      }

      try {
        console.log('Fetching order details for ID:', orderId);
        // Use the correct endpoint matching Pure Meat API: orders/getByOrderId
        const result = await apiCall('post', 'v1/orders/getByOrderId', { id: orderId }, token);
        console.log('Order detail API result:', result);
        
        if (result.success && result.data) {
          // Handle both response formats
          const orderData = result.data.data || result.data;

          setOrder(orderData);
        } else {
          setError(result.message || 'Failed to load order details');
        }
      } catch (err) {
        console.error('Error fetching order details:', err);
        setError('An error occurred while loading order details');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetail();
  }, [orderId, token]);

  const getStatusBadge = (status) => {
    const statusMap = {
      'created': { class: 'warning', text: 'Created' },
      'accepted': { class: 'info', text: 'Accepted' },
      'confirmed': { class: 'info', text: 'Confirmed' },
      'preparing': { class: 'primary', text: 'Preparing' },
      'ongoing': { class: 'primary', text: 'On the Way' },
      'ready': { class: 'success', text: 'Ready' },
      'delivered': { class: 'success', text: 'Delivered' },
      'cancelled': { class: 'danger', text: 'Cancelled' },
      'rejected': { class: 'danger', text: 'Rejected' }
    };
    return statusMap[status] || { class: 'secondary', text: status };
  };

  const getItemImage = (item) => {
    if (item.cover && item.cover.trim()) {
      return item.cover.startsWith('http') 
        ? item.cover 
        : `${API_CONFIG.IMAGE_URL}${item.cover}`;
    }
    return '/assets/img/product/placeholder.png';
  };

  const getVariantDisplay = (item) => {
    // Check if item has variations and a selected variant
    if (item.size === 1 && item.variations) {
      try {
        let variations = item.variations;
        if (typeof variations === 'string') {
          variations = JSON.parse(variations);
        }
        if (Array.isArray(variations) && variations.length > 0) {
          const variantGroup = variations[0];
          if (variantGroup.items && variantGroup.items[item.variant]) {
            return variantGroup.items[item.variant].title || variantGroup.items[item.variant].name;
          }
        }
      } catch (e) {
        console.error('Error parsing variant:', e);
      }
    }
    return null;
  };

  const getMeasurementDisplay = (item) => {
    const measures = [];
    if (item.have_gram === 1 && item.gram) measures.push(`${item.gram} grams`);
    if (item.have_kg === 1 && item.kg) measures.push(`${item.kg} kg`);
    if (item.have_liter === 1 && item.liter) measures.push(`${item.liter} ltr`);
    if (item.have_ml === 1 && item.ml) measures.push(`${item.ml} ml`);
    if (item.have_pcs === 1 && item.pcs) measures.push(`${item.pcs} pcs`);
    return measures.length > 0 ? measures.join(', ') : null;
  };

  if (loading) {
    return (
      <>
        <Header />
        <Breadcrumb title="Order Details" items={[
          { label: 'Home', href: '/' },
          { label: 'My Account', href: '/my-account' },
          { label: 'Order Details' }
        ]} />
        <main className="main__content_wrapper">
          <div className="container py-5">
            <div className="row">
              <div className="col-12">
                <div className="skeleton skeleton-text mb-4" style={{ height: '32px', width: '300px' }}></div>
                <LoadingSkeleton type="card" count={1} />
                <LoadingSkeleton type="list" count={1} />
                <LoadingSkeleton type="card" count={1} />
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (error || !order) {
    return (
      <>
        <Header />
        <Breadcrumb title="Order Details" items={[
          { label: 'Home', href: '/' },
          { label: 'My Account', href: '/my-account' },
          { label: 'Order Details' }
        ]} />
        <main className="main__content_wrapper">
          <div className="container py-5">
            <div className="alert alert-danger">{error || 'Order not found'}</div>
            <Link to="/my-account" className="btn btn-primary">
              <i className="fas fa-arrow-left me-2"></i>Back to My Account
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // Parse order items
  let orderItems = [];
  try {
    orderItems = typeof order.orders === 'string' ? JSON.parse(order.orders) : order.orders || [];
  } catch (e) {
    console.error('Failed to parse order items:', e);
  }

  // Parse order notes for tracking
  let orderNotes = [];
  try {
    orderNotes = typeof order.notes === 'string' ? JSON.parse(order.notes) : order.notes || [];
  } catch (e) {
    console.error('Failed to parse order notes:', e);
  }

  // Parse address
  let deliveryAddress = null;
  if (order.order_to === 'home' && order.address) {
    try {
      deliveryAddress = typeof order.address === 'string' ? JSON.parse(order.address) : order.address;
    } catch (e) {
      deliveryAddress = { address: order.address };
    }
  }

  // Get current status
  let currentStatus = 'created';
  try {
    if (typeof order.status === 'string' && order.status.startsWith('[')) {
      const statusArray = JSON.parse(order.status);
      if (statusArray && statusArray.length > 0) {
        currentStatus = statusArray[0].status;
      }
    } else {
      currentStatus = order.status;
    }
  } catch (e) {
    currentStatus = order.status || 'created';
  }

  const statusInfo = getStatusBadge(currentStatus);

  return (
    <>
      <Header />
      <Breadcrumb title="Order Details" items={[
        { label: 'Home', href: '/' },
        { label: 'My Account', href: '/my-account' },
        { label: 'Order Details' }
      ]} />
      
      <main className="main__content_wrapper">
        <style>{`
          .order-detail-card {
            background: #fff;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
            margin-bottom: 24px;
            overflow: hidden;
          }
          .order-detail-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 24px;
          }
          .order-detail-body {
            padding: 24px;
          }
          .order-info-row {
            display: flex;
            justify-content: space-between;
            padding: 12px 0;
            border-bottom: 1px solid #f0f0f0;
          }
          .order-info-row:last-child {
            border-bottom: none;
          }
          .order-info-label {
            color: #666;
            font-weight: 500;
          }
          .order-info-value {
            color: #111;
            font-weight: 600;
            text-align: right;
          }
          .order-item-card {
            display: flex;
            gap: 16px;
            padding: 16px;
            border: 1px solid #e8e8e8;
            border-radius: 8px;
            margin-bottom: 16px;
            transition: box-shadow 0.2s;
          }
          .order-item-card:hover {
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          }
          .order-item-image {
            width: 80px;
            height: 80px;
            border-radius: 8px;
            object-fit: cover;
            flex-shrink: 0;
          }
          .order-item-details {
            flex: 1;
          }
          .order-item-name {
            font-size: 16px;
            font-weight: 600;
            color: #111;
            margin-bottom: 8px;
          }
          .order-item-meta {
            font-size: 14px;
            color: #666;
            margin-bottom: 4px;
          }
          .order-item-price {
            text-align: right;
            flex-shrink: 0;
          }
          .tracking-timeline {
            position: relative;
            padding-left: 40px;
          }
          .tracking-item {
            position: relative;
            padding-bottom: 24px;
          }
          .tracking-item:last-child {
            padding-bottom: 0;
          }
          .tracking-item::before {
            content: '';
            position: absolute;
            left: -28px;
            top: 8px;
            bottom: -16px;
            width: 2px;
            background: #e0e0e0;
          }
          .tracking-item:last-child::before {
            display: none;
          }
          .tracking-icon {
            position: absolute;
            left: -36px;
            top: 0;
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: #4CAF50;
            border: 3px solid #fff;
            box-shadow: 0 0 0 2px #4CAF50;
          }
          .tracking-title {
            font-weight: 600;
            color: #111;
            margin-bottom: 4px;
          }
          .tracking-time {
            font-size: 13px;
            color: #999;
          }
          .section-title {
            font-size: 18px;
            font-weight: 600;
            color: #111;
            margin-bottom: 16px;
            padding-bottom: 8px;
            border-bottom: 2px solid #667eea;
          }
          @media (max-width: 768px) {
            .order-item-card {
              flex-direction: column;
            }
            .order-item-image {
              width: 100%;
              height: 200px;
            }
            .order-item-price {
              text-align: left;
              margin-top: 8px;
            }
          }
        `}</style>

        <section className="section--padding">
          <div className="container">
            {/* Back Button */}
            <div className="mb-4">
              <Link to="/my-account" className="btn btn-outline-primary">
                <svg className="me-2" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
                Back to My Account
              </Link>
            </div>

            {/* Order Header */}
            <div className="order-detail-card">
              <div className="order-detail-header">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                  <div>
                    <h1 className="mb-2" style={{fontSize: '28px', fontWeight: 700}}>Order #{order.id}</h1>
                    <p className="mb-0 opacity-90">
                      Placed on {new Date(order.date_time || order.created_at).toLocaleDateString('en-US', { 
                        year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' 
                      })}
                    </p>
                  </div>
                  <div>
                    <span className={`badge bg-${statusInfo.class} px-3 py-2`} style={{fontSize: '14px'}}>
                      {statusInfo.text}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              {/* Order Items */}
              <div className="col-lg-8">
                <div className="order-detail-card">
                  <div className="order-detail-body">
                    <h3 className="section-title">Order Items</h3>
                    
                    {orderItems.length === 0 ? (
                      <p className="text-muted">No items found in this order</p>
                    ) : (
                      orderItems.map((item, index) => {
                        const variant = getVariantDisplay(item);
                        const measurement = getMeasurementDisplay(item);
                        const itemPrice = parseFloat(item.sell_price || item.original_price || 0);
                        const itemTotal = itemPrice * (item.quantiy || 1);
                        
                        return (
                          <div key={index} className="order-item-card">
                            <img 
                              src={getItemImage(item)} 
                              alt={item.name}
                              className="order-item-image"
                              onError={(e) => e.target.src = '/assets/img/product/placeholder.png'}
                            />
                            <div className="order-item-details">
                              <h4 className="order-item-name">{item.name}</h4>
                              {variant && (
                                <div className="order-item-meta">
                                  <strong>Variant:</strong> {variant}
                                </div>
                              )}
                              {measurement && (
                                <div className="order-item-meta">
                                  <strong>Size:</strong> {measurement}
                                </div>
                              )}
                              {item.store_name && item.store_name !== 'Default Store' && (
                                <div className="order-item-meta">
                                  <strong>Store:</strong> {item.store_name}
                                </div>
                              )}
                              <div className="order-item-meta">
                                <strong>Quantity:</strong> {item.quantiy || 1}
                              </div>
                              <div className="order-item-meta">
                                <strong>Unit Price:</strong> ${itemPrice.toFixed(2)}
                              </div>
                            </div>
                            <div className="order-item-price">
                              <div style={{fontSize: '18px', fontWeight: 700, color: '#667eea'}}>
                                ${itemTotal.toFixed(2)}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Order Tracking */}
                {orderNotes.length > 0 && (
                  <div className="order-detail-card">
                    <div className="order-detail-body">
                      <h3 className="section-title">Order Tracking</h3>
                      <div className="tracking-timeline">
                        {orderNotes.map((note, index) => (
                          <div key={index} className="tracking-item">
                            <div className="tracking-icon"></div>
                            <div className="tracking-title">{note.value}</div>
                            <div className="tracking-time">{note.time}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Order Summary & Details */}
              <div className="col-lg-4">
                {/* Order Summary */}
                <div className="order-detail-card">
                  <div className="order-detail-body">
                    <h3 className="section-title">Order Summary</h3>
                    <div className="order-info-row">
                      <span className="order-info-label">Subtotal</span>
                      <span className="order-info-value">${parseFloat(order.total || 0).toFixed(2)}</span>
                    </div>
                    {/* <div className="order-info-row">
                      <span className="order-info-label">Tax</span>
                      <span className="order-info-value">${parseFloat(order.tax || 0).toFixed(2)}</span>
                    </div> */}
                    <div className="order-info-row">
                      <span className="order-info-label">Delivery Charge</span>
                      <span className="order-info-value">${parseFloat(order.delivery_charge || 0).toFixed(2)}</span>
                    </div>
                    {order.discount > 0 && (
                      <div className="order-info-row">
                        <span className="order-info-label">Discount</span>
                        <span className="order-info-value text-success">-${parseFloat(order.discount).toFixed(2)}</span>
                      </div>
                    )}
                    {order.wallet_price > 0 && (
                      <div className="order-info-row">
                        <span className="order-info-label">Wallet Used</span>
                        <span className="order-info-value text-success">-${parseFloat(order.wallet_price).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="order-info-row" style={{paddingTop: '16px', borderTop: '2px solid #667eea'}}>
                      <span className="order-info-label" style={{fontSize: '18px', color: '#111'}}>Grand Total</span>
                      <span className="order-info-value" style={{fontSize: '20px', color: '#667eea'}}>${parseFloat(order.grand_total || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Payment & Delivery Info */}
                <div className="order-detail-card">
                  <div className="order-detail-body">
                    <h3 className="section-title">Order Information</h3>
                    <div className="order-info-row">
                      <span className="order-info-label">Order ID</span>
                      <span className="order-info-value">#{order.id}</span>
                    </div>
                    <div className="order-info-row">
                      <span className="order-info-label">Payment Method</span>
                      <span className="order-info-value">{order.paid_method || 'N/A'}</span>
                    </div>
                    <div className="order-info-row">
                      <span className="order-info-label">Payment Status</span>
                      <span className="order-info-value">
                        {order.payStatus === 1 || order.pay_status === 1 ? (
                          <span className="badge bg-success">Paid</span>
                        ) : (
                          <span className="badge bg-warning">Pending</span>
                        )}
                      </span>
                    </div>
                    <div className="order-info-row">
                      <span className="order-info-label">Delivery Type</span>
                      <span className="order-info-value">
                        {order.order_to === 'home' ? 'Home Delivery' : 'Store Pickup'}
                      </span>
                    </div>
                    {deliveryAddress && (
                      <>
                        <div className="order-info-row">
                          <span className="order-info-label">Delivery Address</span>
                          <span className="order-info-value" style={{maxWidth: '60%', textAlign: 'right'}}>
                            {deliveryAddress.house && (!deliveryAddress.address || !deliveryAddress.address.toLowerCase().includes(deliveryAddress.house.toLowerCase())) && `${deliveryAddress.house}, `}
                            {deliveryAddress.address || deliveryAddress.landmark}
                          </span>
                        </div>
                        {deliveryAddress.landmark && (!deliveryAddress.address || !deliveryAddress.address.toLowerCase().includes(deliveryAddress.landmark.toLowerCase())) && (
                          <div className="order-info-row">
                            <span className="order-info-label">Landmark</span>
                            <span className="order-info-value">{deliveryAddress.landmark}</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default OrderDetailPage;